import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { EfficiencyTresholdService } from '@app/efficiency-analysis/services/efficiency-treshold.service';
import { ConsumersService } from '@app/shared/services/api/consumers.service';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { flattenDeep } from 'lodash';
import { Observable, Subject, combineLatest } from 'rxjs';
import { first, map, startWith, switchMap, takeUntil, toArray } from 'rxjs/operators';
import { EfficiencyThreshold } from '@app/efficiency-analysis/models/thresholds/efficiency-threshold';
import { ThresholdControl } from '@app/efficiency-analysis/models/thresholds/threshold-control';
import { EAChildBuildings, EABuildingDetailsAndChildren } from '@app/shared/models/efficiency-analysis/building-details-children';
import { Buildings } from '@app/energy-guard/models/buildings';

@Component({
  selector: 'app-modify-efficiency-treshold',
  templateUrl: './modify-efficiency-treshold.component.html',
  styleUrls: ['./modify-efficiency-treshold.component.scss'],
})
export class ModifyEfficiencyTresholdComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Output() closeEvent: EventEmitter<void> = new EventEmitter<void>();
  public data: ThresholdControl[] = [];

  public efficientTresholdControl = new FormControl(0, [
    Validators.required,
    (control: AbstractControl) => Validators.min(1)(control),
    (control: AbstractControl) => Validators.max(100)(control),
  ]);

  public inefficientTresholdControl = new FormControl(0, [
    Validators.required,
    (control: AbstractControl) => Validators.min(1)(control),
    (control: AbstractControl) => Validators.max(100)(control),
  ]);

  private efficientOldVal: number = 0;
  private inefficientOldVal: number = 0;

  constructor(
    public translate: TranslateService,
    private efficiencyTreshold: EfficiencyTresholdService,
    private efficiencyAnalysis: EfficiencyAnalysisService,
    private consumerService: ConsumersService,
    private overlayLoader: OverlayLoaderService,
    private matDialog: MatDialog
  ) {}

  public ngOnInit(): void {
    this.efficiencyTreshold.treshold
      .pipe(
        switchMap((threshold: EfficiencyThreshold) => {
          this.efficientTresholdControl.setValue(threshold.efficientTreshold || 0);
          this.inefficientTresholdControl.setValue(threshold.inefficientTreshold || 0);
          this.efficientOldVal = threshold.efficientTreshold;
          this.inefficientOldVal = threshold.inefficientTreshold;
          return this.treshold();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([efficient, inefficient]: ThresholdControl[]) => (this.data = [efficient, inefficient]));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private treshold(): Observable<ThresholdControl[]> {
    return combineLatest([
      this.efficientTresholdControl.valueChanges.pipe(
        startWith(this.efficientTresholdControl.value),
        map((value: any) => ({ indicator: 'green', value: value }))
      ),
      this.inefficientTresholdControl.valueChanges.pipe(
        startWith(this.inefficientTresholdControl.value),
        map((value: any) => ({ indicator: 'red', value: value }))
      ),
    ]);
  }

  public numberOnly(event: KeyboardEvent): boolean {
    const allowInput = /^\d$/.test(event.key);
    return allowInput;
  }

  public save(efficientTreshold = this.efficientTresholdControl.value, inefficientTreshold = this.inefficientTresholdControl.value): void {
    this.overlayLoader.isLoading = true;
    this.efficiencyTreshold
      .updateEfficiencyTreshold(String(efficientTreshold), String(inefficientTreshold))
      .pipe(
        switchMap(() => combineLatest([this.updateBuildingColorStatus()]).pipe(toArray())),
        first()
      )
      .subscribe(() => {
        this.overlayLoader.isLoading = false;
        this.openThresholdSavingConfirmationPopup();
      });
  }

  private openThresholdSavingConfirmationPopup(): void {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.efficiencyAnalysis.efficiencyAnalysisConfigDialog.successValuesChangeText'),
      },
      panelClass: ['-md'],
    });
  }

  private updateBuildingColorStatus(): Observable<Buildings>[] {
    return this.getBuildingsWithEfficiencyAnalysis(this.efficiencyAnalysis.buildingsWithEA)
      .map((buildings: EAChildBuildings) => this.setColorStatus(buildings))
      .map((children: EAChildBuildings) => this.consumerService.updateBuilding(children));
  }

  private getBuildingsWithEfficiencyAnalysis(buildingsWithAnalysis: EABuildingDetailsAndChildren[]): EAChildBuildings[] {
    return flattenDeep(buildingsWithAnalysis.map((type: EABuildingDetailsAndChildren) => type.children));
  }

  private setColorStatus(building: EAChildBuildings): EAChildBuildings {
    building.electricityColorStatus = this.efficiencyAnalysis.getEAColorStatusTriColor(building.efficiencyPercentageElectricity);
    building.heatColorStatus = this.efficiencyAnalysis.getEAColorStatusTriColor(building.efficiencyPercentageHeat);
    return building;
  }

  public checkButtonStatus(): boolean {
    const efficient = this.efficientTresholdControl.value;
    const inefficient = this.inefficientTresholdControl.value;
    return (
      efficient == null ||
      inefficient == null ||
      (inefficient !== null && efficient !== null && inefficient <= efficient) ||
      ((efficient === this.efficientOldVal || efficient === this.efficientOldVal) &&
        (inefficient === this.inefficientOldVal || inefficient === this.inefficientOldVal))
    );
  }

  public cancel(): void {
    this.closeEvent.emit();
  }
}
